import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import type { StockPlanningItem } from '../../../lib/api';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import type { LocalColumn } from './DirectDeliveryColumns';
import { getStockHealthColor, getItemCellValue, overLimitReason } from './warehouseWizardGrid';
import DirectProductStar from '../shared/DirectProductStar';
import { isNegativeStockHealth } from '../shared/stockHealth';
import PriorityBadge from '../shared/PriorityBadge';
import { PRIORITY_EDGE_CLASS, PRIORITY_ROW_CLASS } from '../shared/linePriority';
import { warehouseRowTitle } from './warehouseGridColours';

/** Why a row on an ASN cannot change its Priority tick. */
export const PRIORITY_LOCKED_TITLE = "Already on an ASN — priority can't be changed";

/** Sort key for the Priority column: 1 for a ticked row, 0 otherwise. */
export const PRIORITY_SORT_COLUMN = 'priority_rank';

interface WarehouseDeliveryGridProps {
  columns: LocalColumn[];
  items: StockPlanningItem[];
  quantities: Record<number, number>;
  onQtyChange: (itemId: number, value: string) => void;
  /** CR-8613: the Priority tick per row id, for this call-off only. */
  priorities: Record<number, boolean>;
  /** CR-8613: rows already on an ASN, whose tick cannot change. */
  lockedPriority: Record<number, boolean>;
  onPriorityChange: (itemId: number, checked: boolean) => void;
  overInfo: Record<number, { over: boolean; effective: number }>;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
  onHeaderClick: (colId: string) => void;
  onHeaderDoubleClick: (colId: string) => void;
  getAriaSort: (colId: string) => 'ascending' | 'descending' | 'none';
}

/**
 * The stock grid inside the Add Warehouse Delivery modal: one row per part,
 * the chosen columns, a Qty input and a sortable Priority column.
 *
 * CR-8613: Priority is ticked here, per row, and applies to this call-off
 * only — it moved from Stock Planning, where it flagged the part on every
 * open call-off. A row already on an ASN keeps its tick.
 *
 * Row fill — only one is applied, because Tailwind's stylesheet order, not the
 * class order, decides between two background classes:
 * - a priority row keeps its Stock Health warning (red/yellow) when it has
 *   one, and is orange otherwise; its orange edge bar marks it either way;
 * - any other row is blue once it has a quantity, else its Stock Health tint.
 */
export default function WarehouseDeliveryGrid({
  columns, items, quantities, onQtyChange, priorities, lockedPriority, onPriorityChange,
  overInfo, sortColumn, sortDirection, onHeaderClick, onHeaderDoubleClick, getAriaSort,
}: WarehouseDeliveryGridProps) {
  const renderSortIcon = (colId: string) => {
    if (sortColumn !== colId) return <ArrowUpDown className="h-3 w-3 text-muted-foreground/50 shrink-0" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3 w-3 text-primary shrink-0" />
      : <ArrowDown className="h-3 w-3 text-primary shrink-0" />;
  };

  const rowClass = (item: StockPlanningItem): string => {
    const health = getStockHealthColor(item.stock_health_min, item.stock_health_max);
    if (priorities[item.id]) return health || PRIORITY_ROW_CLASS;
    if (quantities[item.id] > 0) return 'bg-blue-50 hover:bg-muted/50';
    return `hover:bg-muted/50 ${health}`;
  };

  /** CR-8613: a coloured row says why it is that colour. */
  const rowTitle = (item: StockPlanningItem): string | undefined => (
    warehouseRowTitle(item, Boolean(priorities[item.id]), quantities[item.id] || 0)
  );

  const sortableHeader = (id: string, label: string, className: string, rightAlign: boolean) => (
    <th
      key={id}
      onClick={() => onHeaderClick(id)}
      onDoubleClick={() => onHeaderDoubleClick(id)}
      title={id === PRIORITY_SORT_COLUMN
        ? 'Tick a row to make it a priority on this call-off only. Click to sort.'
        : 'Click to sort • double-click to bring negative values to the top'}
      aria-sort={getAriaSort(id)}
      className={`py-2.5 px-2 whitespace-normal break-words align-top cursor-pointer select-none ${
        sortColumn === id ? 'bg-muted-foreground/10' : ''
      } ${className}`}
    >
      <div className={`flex items-start gap-1 ${rightAlign ? 'justify-end' : 'justify-between'}`}>
        <span>{label}</span>
        {renderSortIcon(id)}
      </div>
    </th>
  );

  return (
    <table className="w-full text-sm">
      <thead className="sticky top-0 z-20">
        <tr className="text-left text-xs font-semibold text-foreground bg-muted border-b shadow-[0_2px_4px_-2px_rgba(0,0,0,0.08)]">
          {columns.map(col => {
            const rightAlign = col.id !== 'part_number' && col.id !== 'description';
            return sortableHeader(
              col.id, col.label,
              `min-w-[80px] hover:bg-muted-foreground/10 ${rightAlign ? 'text-right' : ''}`,
              rightAlign,
            );
          })}
          <th className="py-2.5 px-2 text-right w-24 bg-yellow-100 text-yellow-900 align-top">Qty</th>
          {sortableHeader(
            PRIORITY_SORT_COLUMN, 'Priority',
            'w-20 bg-orange-100 text-orange-900 hover:bg-orange-200',
            false,
          )}
        </tr>
      </thead>
      <tbody className="divide-y">
        {items.map(item => (
          <tr key={item.id} className={rowClass(item)} title={rowTitle(item)}>
            {columns.map((col, idx) => (
              <td key={col.id} className={`py-1.5 px-2 align-top whitespace-normal break-words ${
                col.id === 'part_number' ? 'font-mono text-xs' :
                col.id === 'description' ? 'text-xs max-w-[200px]' :
                'text-right'
              } ${isNegativeStockHealth(col.id, getItemCellValue(item, col.id)) ? 'text-red-600 font-medium' : ''} ${
                idx === 0 && priorities[item.id] ? PRIORITY_EDGE_CLASS : ''
              }`}>
                {col.id === 'part_number' && item.is_direct_product && <DirectProductStar />}
                {getItemCellValue(item, col.id)}
              </td>
            ))}
            <td
              className={`py-1.5 px-2 ${overInfo[item.id]?.over ? 'bg-red-50' : 'bg-yellow-50'}`}
              title={overInfo[item.id]?.over
                ? 'Red: more than is available'
                : 'Yellow: type the quantity here'}
            >
              <Input
                type="number"
                min={0}
                className={`h-7 w-20 text-right text-sm ${
                  overInfo[item.id]?.over
                    ? 'border-red-500 bg-red-50 text-red-700 focus-visible:ring-red-500'
                    : ''
                }`}
                value={quantities[item.id] || ''}
                onChange={(e) => onQtyChange(item.id, e.target.value)}
                placeholder="0"
              />
              {overInfo[item.id]?.over && (
                <div className="text-[10px] text-red-700 mt-0.5">
                  {overLimitReason(item, overInfo[item.id].effective)}
                </div>
              )}
            </td>
            <td className="py-1.5 px-2 align-middle">
              <div className="flex items-center justify-center gap-1">
                <Checkbox
                  aria-label={`Priority for ${item.part_number}`}
                  title={lockedPriority[item.id]
                    ? PRIORITY_LOCKED_TITLE
                    : 'Priority on this call-off only'}
                  checked={Boolean(priorities[item.id])}
                  disabled={Boolean(lockedPriority[item.id])}
                  onCheckedChange={(v) => onPriorityChange(item.id, v === true)}
                />
                {priorities[item.id] && <PriorityBadge title="Priority on this call-off" />}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
